import * as React from "react";
import { Alert, Box, Button, Link, Skeleton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { ChartLineIcon } from "@phosphor-icons/react/dist/ssr/ChartLine";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { ProhibitIcon } from "@phosphor-icons/react/dist/ssr/Prohibit";
import { ReceiptIcon } from "@phosphor-icons/react/dist/ssr/Receipt";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { RequestState } from "@/hooks/api/use-campaign-requests";
import {
	useCampaignRequest,
	useCampaignRequestEvents,
	useCampaignRequestQuotes,
	useSubmitCampaignRequest,
	useTransitionCampaignRequest,
} from "@/hooks/api/use-campaign-requests";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { ConfirmModal } from "@/components/confirm-modal/confirm-modal";
import { RouterLink } from "@/components/core/link";

import { RequestNotFound, StateChip } from "../components";
import { canConfirm, canTransition, formatDate, objectiveLabel } from "../constants";
import { humaniseEnum, requestTitle } from "../humanise";
import { ActivityTab } from "./activity-tab";
import { ConfigurationTab } from "./configuration-tab";
import { DocumentsTab } from "./documents-tab";
import { QuoteTab } from "./quote-tab";
import { SummaryTab } from "./summary-tab";

/**
 * One request after submit — UX §12, "what's happening and what it's cost".
 *
 * This was one column of eleven stacked panels: every fact the request holds, at the same weight,
 * in one scroll. A client opening it to ask "is it running yet, and what do you still need from
 * me" had to read the compliance attestations and the notification templates to find out.
 *
 * So it is five tabs, and each answers one question. **Summary** is the five-second read: where it
 * is, what is outstanding, when it dials, what it costs. **Configuration** is the campaign as it
 * was set up. **Quote** is the price and every version of it. **Documents** is the paperwork.
 * **Activity** is the history, in their words rather than ours.
 *
 * The header carries only what is true of the request as a whole, and only the moves that are
 * actually available. Four permanently greyed-out buttons under a line explaining why they are
 * grey is four dead controls and a sentence about the state machine; a client is shown the moves
 * they can make, and the state chip beside the title already says where the request stands.
 */

const TABS = [
	{ value: "summary", label: "Summary" },
	// Not "Configuration". The tab holds the agents, markets, outcomes and rules this campaign
	// runs on, and a client does not configure anything — they agreed to it.
	{ value: "configuration", label: "How It Runs" },
	{ value: "quote", label: "Quote" },
	{ value: "documents", label: "Documents" },
	{ value: "activity", label: "Activity" },
] as const;

type TabValue = (typeof TABS)[number]["value"];

const TAB_VALUES = new Set<string>(TABS.map((tab) => tab.value));

/** A phrase written to follow a separator, promoted to the front of a line. */
function sentenceStart(text: string): string {
	return text.charAt(0).toUpperCase() + text.slice(1);
}

export function CampaignRequestView(): React.JSX.Element {
	const { requestId } = useParams<{ requestId: string }>();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { data: request, isLoading, isError } = useCampaignRequest(requestId);
	const { data: events = [], isLoading: isEventsLoading } = useCampaignRequestEvents(requestId);
	const { data: quotes = [], isLoading: isQuotesLoading } = useCampaignRequestQuotes(requestId);

	const submitMutation = useSubmitCampaignRequest();
	const transitionMutation = useTransitionCampaignRequest();

	const [pendingMove, setPendingMove] = React.useState<RequestState | null>(null);

	// The tab lives in the URL so a client can send someone straight to their quote, and so the
	// browser's own back button steps back through the tabs they opened.
	const requestedTab = searchParams.get("tab") ?? "";
	const activeTab: TabValue = TAB_VALUES.has(requestedTab) ? (requestedTab as TabValue) : "summary";

	const openTab = React.useCallback(
		(value: TabValue): void => {
			setSearchParams(
				(previous) => {
					const next = new URLSearchParams(previous);
					next.set("tab", value);
					return next;
				},
				{ replace: false }
			);
		},
		[setSearchParams]
	);

	if (isLoading) {
		return (
			<Box sx={{ m: "var(--Content-margin)", p: "var(--Content-padding)", width: "var(--Content-width)" }}>
				<Stack spacing={2}>
					<Skeleton height={48} variant="rounded" />
					<Skeleton height={120} variant="rounded" />
					<Skeleton height={320} variant="rounded" />
				</Stack>
			</Box>
		);
	}

	if (isError || !request) {
		return <RequestNotFound backLabel="Back to campaign requests" backTo="/campaign-requests" />;
	}

	const confirmedLine = request.confirmedAt
		? `confirmed ${formatDate(request.confirmedAt)}${request.confirmedByName ? ` by ${request.confirmedByName}` : ""}`
		: `raised ${formatDate(request.createdAt)}`;

	/**
	 * The line under the title: their reference, when it was settled, what it is for.
	 *
	 * Assembled rather than concatenated so it can never open mid-sentence. A request with no
	 * client reference of its own used to start this line with a lower-case "confirmed", because
	 * the phrase was written to follow a separator that was not always there.
	 */
	const reference = request.clientReference?.trim() ?? "";
	const objective = request.objective ? humaniseEnum(objectiveLabel(request.objective)) : "";
	const subtitle = [
		reference === "" ? sentenceStart(confirmedLine) : reference,
		reference === "" ? "" : confirmedLine,
		objective,
	]
		.filter((part) => part !== "")
		.join(" · ");

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={3}>
					<Stack spacing={2}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href="/campaign-requests"
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Campaign Requests
							</Link>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace="pre">
								{requestTitle(request.title)}
							</Typography>
						</Breadcrumbs>

						<Stack
							direction={{ xs: "column", md: "row" }}
							spacing={2}
							sx={{ alignItems: { md: "flex-start" }, justifyContent: "space-between" }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
									<Typography variant="h4">{requestTitle(request.title)}</Typography>
									<StateChip state={request.state} variant="client" size="medium" />
								</Stack>

								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									{subtitle}
								</Typography>
							</Box>

							<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
								{request.campaignProgrammeId ? (
									<Button
										startIcon={<ChartLineIcon />}
										variant="contained"
										onClick={() => navigate(`/campaign-requests/${request.id}/live`)}
									>
										Live Results
									</Button>
								) : null}

								{canTransition(request.state, "submitted") ? (
									<Button
										variant="outlined"
										disabled={submitMutation.isPending}
										onClick={() => submitMutation.mutate({ id: request.id })}
									>
										Send to Us
									</Button>
								) : null}

								{canConfirm(request.state) ? (
									<Button
										startIcon={<ReceiptIcon />}
										variant="contained"
										onClick={() => navigate(`/campaign-requests/create?request=${request.id}&step=8`)}
									>
										Confirm Quote
									</Button>
								) : null}

								{!request.provisionedAt && canTransition(request.state, "changes_requested") ? (
									<Button
										startIcon={<PencilSimpleIcon />}
										variant="outlined"
										disabled={transitionMutation.isPending}
										onClick={() => setPendingMove("changes_requested")}
									>
										Ask for Changes
									</Button>
								) : null}

								{canTransition(request.state, "cancelled") ? (
									<Button
										color="error"
										startIcon={<ProhibitIcon />}
										variant="outlined"
										disabled={transitionMutation.isPending}
										onClick={() => setPendingMove("cancelled")}
									>
										Cancel
									</Button>
								) : null}
							</Stack>
						</Stack>
					</Stack>

					<Box sx={{ borderBottom: "1px solid var(--mui-palette-divider)" }}>
						{request.preparationStatus ? (
							<Alert severity="info" sx={{ mb: 2 }}>
								{
									{
										queued: "Your uploads are queued for preparation.",
										running: "We are preparing your leads and call flows.",
										review: "Your campaign is ready for our team's review.",
										approved: "Our team has approved your campaign preparation.",
										failed: "Our team is reviewing an issue with campaign preparation.",
									}[request.preparationStatus]
								}
								{request.preparationStatus === "approved" && request.proving ? (
									<div>
										{request.proving.status === "passed"
											? "Quality checks passed. Your campaign is ready for the next setup step."
											: ["queued", "running"].includes(request.proving.status)
												? `We are testing your agent before launch (${request.proving.passed}/${request.proving.total} conversations passed).`
												: "Our team is completing quality checks before launch."}
									</div>
								) : null}
							</Alert>
						) : null}
						<Tabs
							value={activeTab}
							variant="scrollable"
							onChange={(_event, value: TabValue) => {
								openTab(value);
							}}
						>
							{TABS.map((tab) => (
								<Tab key={tab.value} label={tab.label} value={tab.value} sx={{ minHeight: "auto" }} tabIndex={0} />
							))}
						</Tabs>
					</Box>

					{activeTab === "summary" ? (
						<SummaryTab
							request={request}
							events={events}
							onOpenActivity={() => {
								openTab("activity");
							}}
						/>
					) : null}

					{activeTab === "configuration" ? <ConfigurationTab request={request} /> : null}

					{activeTab === "quote" ? (
						<QuoteTab request={request} quotes={quotes} isQuotesLoading={isQuotesLoading} />
					) : null}

					{activeTab === "documents" ? <DocumentsTab request={request} /> : null}

					{activeTab === "activity" ? (
						<ActivityTab request={request} events={events} isLoading={isEventsLoading} />
					) : null}
				</Stack>
			</Box>

			<ConfirmModal
				open={pendingMove !== null}
				onClose={() => setPendingMove(null)}
				onSumbit={() => {
					if (pendingMove) transitionMutation.mutate({ id: request.id, state: pendingMove });
				}}
				title={pendingMove === "cancelled" ? "Cancel This Request" : "Ask for Changes"}
				description={
					pendingMove === "cancelled"
						? "Nothing further will be built or dialled on this request. Work already done stays billable."
						: "We will stop preparing and come back to you. Any quote on the table has to be re-issued afterwards."
				}
			/>
		</React.Fragment>
	);
}
